import { Box, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { styles } from "@/pages/Settings/stylesSettings";
import ArrowDawnSvg from "@/assets/svg/ArrowDawnSvg";

const options = [
  "Scalping",
  "Day Trading",
  "Swing Trading",
  "Position Trading",
  "Algorithmic Trading",
  "Futures Trading",
  "Penny Stock Trading",
];

type Props = {
  onSelect: (style: string) => void;
  defaultValue?: string;
};

const TradingStyle = ({ onSelect, defaultValue }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [customValue, setCustomValue] = useState("");

  // Визначаємо чи defaultValue є в списку options
  useEffect(() => {
    if (defaultValue) {
      const isInOptions = options.includes(defaultValue);
      if (isInOptions) {
        setSelected(defaultValue);
        setCustomValue("");
      } else {
        setSelected(null);
        setCustomValue(defaultValue);
      }
    }
  }, [defaultValue]);

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const handleSelect = (value: string) => {
    setSelected(value);
    setCustomValue("");
    onSelect(value);
    setIsOpen(false);
  };

  const handleCustomValueChange = (value: string) => {
    setCustomValue(value);
    if (value.trim()) {
      setSelected(null);
      onSelect(value.trim());
    }
  };

  const handleClose = () => {
    if (!selected) setIsOpen(false);
  };

  const displayValue = selected || (customValue ? "Custom Style" : "Select Trading Style");
  const hasValue = selected || customValue;

  return (
    <Box>
      {/* Select dropdown */}
      <Box sx={{ position: "relative" }}>
        <Typography sx={styles.label}>Trading Style</Typography>

        <Box
          sx={{
            ...styles.tradingStyleWrapper,
            background: isOpen || hasValue ? "#FFFFFF" : "#F7F9FB",
            boxShadow: isOpen || hasValue ? "0px 0px 4px 0px #0000001A" : "none",
            paddingRight: "16px",
          }}
          onClick={toggleOpen}
          onBlur={handleClose}
          tabIndex={0}
        >
          <Typography
            sx={{
              ...styles.selectPlaceholder,
              color: hasValue ? "#2A2D31" : "#9DA6B0",
            }}
          >
            {displayValue}
          </Typography>

          <Box
            sx={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "0.2s",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ArrowDawnSvg color={hasValue ? "#2A2D31" : "#9DA6B0"} />
          </Box>
        </Box>

        {isOpen && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              zIndex: 10,
              width: "100%",
              maxHeight: "310px",
              borderRadius: "16px",
              padding: "8px",
              background: "#FFF",
              boxShadow: "0px 0px 16px 0px #0000001A",
              marginTop: "8px",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
            }}
            className="hide-scrollbar"
          >
            {options.map((option) => (
              <Box
                key={option}
                sx={{
                  width: "100%",
                  height: "42px",
                  borderRadius: "8px",
                  padding: "10px 8px",
                  background: selected === option ? "#F4F5F6" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": {
                    background: "#F4F5F6",
                  },
                }}
                onMouseDown={() => handleSelect(option)}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "22px",
                    color: "#2A2D31",
                  }}
                >
                  {option}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Custom input */}
      <Box sx={{ mt: "20px" }}>
        <Typography sx={styles.label}>Or enter custom style</Typography>
        
        <Box
          sx={{
            ...styles.tradingStyleWrapper,
            background: customValue ? "#FFFFFF" : "#F7F9FB",
            boxShadow: customValue ? "0px 0px 4px 0px #0000001A" : "none",
            paddingRight: "16px",
          }}
        >
          <input
            placeholder="Type your custom trading style"
            value={customValue}
            onChange={(e) => handleCustomValueChange(e.target.value)}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              background: "transparent",
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "22px",
              color: customValue ? "#2A2D31" : "#9DA6B0",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TradingStyle;